import { Box, Button, Flex, Icon, Skeleton, Table, Text, VStack } from '@chakra-ui/react'
import { LuScanText, LuSearch, LuTriangleAlert } from 'react-icons/lu'
import { mapInvoiceStatus } from '../domain/statusMapping'
import type { DashboardFilter } from '../domain/types'
import { InvoiceStatusBadge } from './InvoiceStatusBadge'
import { EmptyStateCard } from '@/components/widget-wrappers/EmptyStateCard'
import type { ServiceBillingInvoice } from '@/components/invoices/invoice.types'

type Props = {
  rows: ServiceBillingInvoice[]
  isLoading?: boolean
  /** true when the invoice load failed (shows the error/retry state) */
  error?: boolean
  /** retry handler for the error state */
  onRetry?: () => void
  /** true when a search term or status/date filter is active (drives the no-result copy) */
  isFiltered?: boolean
  /** active tab, used to tailor the empty-state description */
  filter?: DashboardFilter
  onPreview?: (invoice: ServiceBillingInvoice) => void
  onRowClick?: (invoice: ServiceBillingInvoice) => void
}

const COLUMNS = [
  { key: 'sib', label: 'SIB', w: '22%' },
  { key: 'gross', label: 'Bruttobetrag', w: '12%' },
  { key: 'date', label: 'Datum', w: '11%' },
  { key: 'number', label: 'Rechnungsnummer', w: '14%' },
  { key: 'period', label: 'Abrechnungszeitraum', w: '17%' },
  { key: 'status', label: 'Rechnungsstatus', w: '24%' },
] as const

const SKELETON_ROWS = 8

function formatDate(value: string) {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value))
}

function formatBillingPeriod(from: string, to: string) {
  return `${formatDate(from)} – ${formatDate(to)}`
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0)
}

function emptyDescriptionForFilter(filter?: DashboardFilter) {
  if (filter === 'ARCHIVE') return 'Es liegen keine archivierten Rechnungen vor.'
  if (filter === 'ALL') return 'Es liegen keine Rechnungen vor.'
  return 'Es liegen keine offenen Rechnungen vor.'
}

function TableShell({ children }: { children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
      <Table.Root tableLayout="fixed">
        <Table.Header bg="bg">
          <Table.Row>
            {COLUMNS.map((col, idx) => (
              <Table.ColumnHeader
                key={col.key}
                w={col.w}
                px="4"
                py="5"
                fontSize="md"
                fontWeight="700"
                color="fg.default"
                borderBottomWidth="1px"
                borderColor="border"
                borderRightWidth={idx < COLUMNS.length - 1 ? '1px' : '0'}
              >
                {col.label}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>{children}</Table.Body>
      </Table.Root>
    </Box>
  )
}

function EmptyState({
  variant,
  filter,
}: {
  variant: 'no-data' | 'no-results'
  filter?: DashboardFilter
}) {
  const isSearch = variant === 'no-results'
  return (
    <Flex
      minH="200px"
      align="center"
      justify="center"
      borderWidth="1px"
      borderColor="border"
      borderRadius="xl"
      bg="bg"
    >
      <VStack gap={3} px={6} textAlign="center">
        <Icon as={isSearch ? LuSearch : LuScanText} boxSize="28px" color="fg.muted" />
        <Text fontSize="lg" fontWeight="700" color="fg.default">
          {isSearch ? 'Kein Ergebnis' : 'Keine Rechnung verfügbar'}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {isSearch
            ? 'Keine Treffer. Versuchen Sie einen anderen Suchbegriff oder Filter.'
            : emptyDescriptionForFilter(filter)}
        </Text>
      </VStack>
    </Flex>
  )
}

export function ServiceBillingTable({
  rows,
  isLoading = false,
  error = false,
  onRetry,
  isFiltered = false,
  filter,
  onRowClick,
}: Props) {
  if (error) {
    return (
      <EmptyStateCard
        minH="200px"
        icon={<LuTriangleAlert size={32} />}
        title="Fehler beim Laden"
        description="Die Rechnungen konnten nicht geladen werden."
        action={
          onRetry ? (
            <Button variant="outline" onClick={onRetry}>
              Erneut versuchen
            </Button>
          ) : undefined
        }
      />
    )
  }

  if (isLoading) {
    return (
      <TableShell>
        {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
          <Table.Row key={`skeleton-${rowIdx}`}>
            {COLUMNS.map((col, idx) => (
              <Table.Cell
                key={col.key}
                px="4"
                py="5"
                borderBottomWidth="1px"
                borderColor="border"
                borderRightWidth={idx < COLUMNS.length - 1 ? '1px' : '0'}
                verticalAlign="middle"
              >
                <Skeleton h="16px" rounded="md" />
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </TableShell>
    )
  }

  if (rows.length === 0) {
    return <EmptyState variant={isFiltered ? 'no-results' : 'no-data'} filter={filter} />
  }

  return (
    <TableShell>
      {rows.map(invoice => {
        const status = mapInvoiceStatus(
          invoice.approvalStatus,
          Boolean(invoice.sentAt),
          invoice.status === 'DRAFT',
          invoice.status === 'CANCELED'
        )

        return (
          <Table.Row
            key={invoice.id}
            cursor="pointer"
            onClick={() => onRowClick?.(invoice)}
            _hover={{ bg: 'bg.subtle' }}
          >
            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" fontWeight="500" color="fg.default">
                {invoice.consultantName}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {formatCurrency(invoice.totalGross)}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {formatDate(invoice.sentAt ?? invoice.finalizedAt ?? invoice.createdAt)}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {invoice.invoiceNumber || '—'}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {formatBillingPeriod(invoice.periodFrom, invoice.periodTo)}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <InvoiceStatusBadge status={status} />
            </Table.Cell>
          </Table.Row>
        )
      })}
    </TableShell>
  )
}
